import React, { useState, useCallback, useEffect, useContext } from "react";
import {
  PButtonPure,
  PSegmentedControl,
  PSegmentedControlItem,
  PSpinner,
} from "@porsche-design-system/components-react";
import "./TimeSelection.css";
import { useGetTileBaseStyleDrop } from "MainApp/assets/visual/styles/globalStyles/globalJsxStyles";
import {
  spacingFluidLarge,
  spacingFluidXSmall,
} from "@porsche-design-system/components-js/styles";
import BlurFade from "@/components/magicui/blur-fade";
import { useTranslation } from "react-i18next";
import { AuthContext } from "MainApp/assets/utils/context/AuthContext";
import CButton from "Components/CComponents/CButton/CButton";
import CText from "Components/CComponents/CText/CText";
import { formatBusinessDateFromEpochSeconds } from "./reservationDateUtils";

const TimeSelection = ({
  date,
  onTimeSelect,
  onBack,
  isPhone,
  tourUUID,
  selectedDuration,
}) => {
  const { axiosInstance } = useContext(AuthContext);
  const tileBasedStyle = useGetTileBaseStyleDrop();
  const { t } = useTranslation("timeSelection");

  const [currentValue, setCurrentValue] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAvailableTimes = useCallback(
    async (selectedDate) => {
      setLoading(true);
      try {
        const formattedDate = formatBusinessDateFromEpochSeconds(selectedDate);
        if (!formattedDate) {
          setAvailableTimes([]);
          return;
        }

        const response = await axiosInstance.get(
          `/toursAPI/${tourUUID}/get-available-hours`,
          {
            params: {
              date: formattedDate,
              duration: Number(selectedDuration || 0) || undefined,
            },
          }
        );

        setAvailableTimes(
          Array.isArray(response.data?.available_hours)
            ? response.data.available_hours
            : []
        );
        setCurrentValue(null);
      } catch (error) {
        console.error("Error fetching available times", error);
        setAvailableTimes([]);
        setCurrentValue(null);
      } finally {
        setLoading(false);
      }
    },
    [axiosInstance, tourUUID, selectedDuration]
  );

  useEffect(() => {
    fetchAvailableTimes(date);
  }, [date, fetchAvailableTimes]);

  const onUpdate = useCallback((e) => {
    setCurrentValue(e.detail.value);
  }, []);

  const handleTimeSelect = () => {
    if (currentValue !== null) {
      onTimeSelect(availableTimes[currentValue]);
    }
  };

  return (
    <BlurFade className="fadeInCalendar">
      <div className="time-selection">
        <PButtonPure
          className="back-button"
          icon="arrow-left"
          hideLabel={true}
          onClick={onBack}
          style={{ marginLeft: isPhone ? "" : spacingFluidXSmall }}
        />
        {loading ? (
          <div className="spinnerContainerTimeSel">
            <PSpinner className="spinnerTimeSel" />
            <CText>{t("loadingHours", "Cargando horas disponibles...")}</CText>
          </div>
        ) : (
          <div
            className="time-selection-sticky"
            style={{ paddingTop: spacingFluidLarge }}
          >
            <BlurFade className="flex w-full justify-center flex-col items-center">
              <div
                className="segmented-control-container"
                style={tileBasedStyle}
              >
                <CText size="large">{t("head")}</CText>
                <BlurFade>
                  {availableTimes.length > 0 ? (
                    <PSegmentedControl
                      value={currentValue}
                      onUpdate={onUpdate}
                      className="pSegmentedControlStyle"
                    >
                      {availableTimes.map((time, index) => (
                        <PSegmentedControlItem
                          key={time}
                          value={index}
                          className="pSegmentedControlItemStyle"
                        >
                          {time}
                        </PSegmentedControlItem>
                      ))}
                    </PSegmentedControl>
                  ) : (
                    <CText>
                      {t(
                        "noHoursAvailable",
                        "No hay horas disponibles para esta fecha."
                      )}
                    </CText>
                  )}
                </BlurFade>
              </div>
              <CButton
                className="select-time-button"
                onClick={handleTimeSelect}
                disabled={currentValue === null}
              >
                {t("confirmButton")}
              </CButton>
            </BlurFade>
            {currentValue !== null && (
              <CText>
                {t("content")} {availableTimes[currentValue]}
              </CText>
            )}
          </div>
        )}
      </div>
    </BlurFade>
  );
};

export default TimeSelection;
